/**
 * Reconciliation screen (M4/M5) — the flagship financial-recovery UI.
 *
 * "The killer feature": it reconstructs the money flow of every payout from the
 * OTA booking through channel commission and tax to the expected net, then sets
 * that against the bank credit ingested from the Open Banking feed — so an
 * operator sees, at a glance, whether the money that should have arrived
 * actually did.
 *
 * Reshaped top-down to the reference layout:
 *   1. Auto-match FLOW banner — Gross → Commission → Tax → Expected net → Bank
 *      credit, with a variance callout bar (FR-4.8, apiReconFlow).
 *   2. State-count cards — Matched / Variance / Pending / Unmatched, each a
 *      count "/ N" with a state-tinted glyph (apiReconSummary counts).
 *   3. Transaction ledger — a searchable, CSV-exportable table reconstructing
 *      each match (ref, guest, channel, gross, commission, net, bank, status),
 *      with per-row close-to-lock (FR-4.4/4.5/4.12).
 *   4. Action bar — seed demo data / run reconciliation (existing handlers).
 *   5. Existing DEPTH kept as expandable <details>: per-match variance
 *      classification + cited precedent, the channel commission model, and the
 *      raw expected/actual ledgers (FR-4.6/4.7).
 *
 * All backend access goes through the fetch-based `../services.js` layer (web
 * boundary: no backend runtime import). Money arrives as integer-minor STRINGS
 * and is shown via `formatMoney(Number(x), currency)`. API error envelopes are
 * surfaced through the shared accessible {@link UserMessage} component.
 *
 * SSR-safe: the initial render is a pure loading state; every network call, and
 * every Date/Blob use, runs inside an effect or an event handler, so the server
 * render matches hydration.
 */
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from "react";
import {
  UserMessage,
  toUserMessage,
  type UserMessageModel,
} from "../../components/UserMessage.js";
import {
  apiCloseMatch,
  apiListActual,
  apiListChannels,
  apiListExpected,
  apiListMatches,
  apiReconFlow,
  apiReconSummary,
  apiRunReconciliation,
  apiSeedReconDemo,
  asError,
  formatMoney,
  type ReconActual,
  type ReconChannel,
  type ReconExpected,
  type ReconFlowSummary,
  type ReconMatch,
  type ReconRunResult,
  type ReconSeedResult,
  type ReconSummary,
} from "../services.js";
import { MoneyValue } from "../components/MoneyValue.js";

/** Fallback currency when a row/summary somehow omits one. */
const FALLBACK_CURRENCY = "INR";

/** Render a basis-points value as a percentage string (1500 → "15.00%"). */
function formatBps(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

/** Map a match state to its badge modifier class (kept for the detail views). */
function stateBadgeClass(state: ReconMatch["state"]): string {
  return `recon-state recon-state--${state}`;
}

// ---------------------------------------------------------------------------
// Channel → colour (mirrors BookingsCalendar: a small brand-mapped set, with a
// stable hash fallback for unknown channels). Used for the ledger channel dot.
// ---------------------------------------------------------------------------

const CHANNEL_COLORS: Record<string, string> = {
  Direct: "#1D35A9", // Primary blue
  "Booking.com": "#0067B1", // Deep blue
  Airbnb: "#B42318", // Red
  Agoda: "#F79009", // Amber
  Expedia: "#667085", // Slate gray
  MakeMyTrip: "#12B76A", // Green
  Goibibo: "#00558F", // Darkest blue
};

const FALLBACK_COLORS = ["#1D35A9", "#12B76A", "#F79009", "#667085", "#B42318"];

/** Resolve a channel name to a stable brand-derived colour. */
function channelColor(channel: string): string {
  const known = CHANNEL_COLORS[channel];
  if (known !== undefined) return known;
  let h = 0;
  for (let i = 0; i < channel.length; i += 1) {
    h = (h * 31 + channel.charCodeAt(i)) >>> 0;
  }
  return FALLBACK_COLORS[h % FALLBACK_COLORS.length] as string;
}

// ===========================================================================
// 1. Auto-match flow banner (FR-4.8, apiReconFlow)
// ===========================================================================

/** The five money-flow steps, in booking → bank order. */
const FLOW_STEPS: readonly {
  readonly key:
    | "grossMinor"
    | "commissionMinor"
    | "taxMinor"
    | "expectedNetMinor"
    | "bankCreditMinor";
  readonly label: string;
  readonly hint: string;
  /** Show as a negative/clay subtraction figure. */
  readonly minus?: boolean;
  /** The single gold element on this banner. */
  readonly gold?: boolean;
}[] = [
  { key: "grossMinor", label: "Gross booking", hint: "OTA inventory value" },
  { key: "commissionMinor", label: "Commission", hint: "Channel fees", minus: true },
  { key: "taxMinor", label: "Tax", hint: "GST / VAT" },
  { key: "expectedNetMinor", label: "Expected net", hint: "Auto-calculated" },
  { key: "bankCreditMinor", label: "Bank credit", hint: "Open Banking feed", gold: true },
];

function FlowBanner({ flow }: { readonly flow: ReconFlowSummary }): ReactElement {
  const currency = flow.currency || FALLBACK_CURRENCY;
  const variance = Number(flow.varianceMinor);
  const hasVariance = variance !== 0;
  return (
    <section className="recon-flow" aria-label="Auto-match money flow">
      <div className="recon-flow__head">
        <p className="recon-flow__eyebrow">
          Auto-match flow · Booking → OTA → Bank
        </p>
        <p className="recon-flow__count">
          {flow.transactionCount} transaction
          {flow.transactionCount === 1 ? "" : "s"}
        </p>
      </div>
      <div className="recon-flow__strip">
        {FLOW_STEPS.map((step, index) => {
          const raw = Number(flow[step.key]);
          const valueClass = step.gold
            ? "recon-flow__value recon-flow__value--gold"
            : step.minus
              ? "recon-flow__value recon-flow__value--minus"
              : "recon-flow__value";
          // A "minus" step is a subtraction in the flow — render it as a
          // negative so the shared compact treatment shows the sign, and the
          // hover tooltip shows the exact negative amount.
          const signedMinor = step.minus ? -Math.abs(raw) : raw;
          return (
            <Fragment key={step.key}>
              <div className="recon-flow__step">
                <p className="recon-flow__label">{step.label}</p>
                <MoneyValue minor={signedMinor} currency={currency} className={valueClass} />
                <p className="recon-flow__hint">{step.hint}</p>
              </div>
              {index < FLOW_STEPS.length - 1 ? (
                <span className="recon-flow__arrow" aria-hidden="true">
                  →
                </span>
              ) : null}
            </Fragment>
          );
        })}
      </div>
      {hasVariance ? (
        <div className="recon-callout recon-callout--attention" role="status">
          <span>
            Net variance of {formatMoney(variance, currency)} requires attention.
          </span>
          <span className="recon-callout__amount">
            {formatMoney(variance, currency)}
          </span>
        </div>
      ) : (
        <div className="recon-callout recon-callout--calm" role="status">
          <span>Every payout reconciled.</span>
        </div>
      )}
    </section>
  );
}

// ===========================================================================
// 2. State-count cards (apiReconSummary counts)
// ===========================================================================

const STATE_CARDS: readonly {
  readonly key: keyof ReconSummary["counts"];
  readonly label: string;
  readonly tone: "ok" | "attention" | "pending" | "critical";
}[] = [
  { key: "matched", label: "Matched", tone: "ok" },
  { key: "variance", label: "Variance", tone: "attention" },
  { key: "pending", label: "Pending", tone: "pending" },
  { key: "unmatched", label: "Unmatched", tone: "critical" },
];

function StateCards({ summary }: { readonly summary: ReconSummary }): ReactElement {
  const total =
    summary.counts.matched +
    summary.counts.variance +
    summary.counts.pending +
    summary.counts.unmatched;
  return (
    <div
      className="console-card-grid"
      aria-label="Reconciliation state counts"
    >
      {STATE_CARDS.map((card) => (
        <div className="console-card" key={card.key}>
          <div className="recon-statecard__head">
            <p className="console-card__label">{card.label}</p>
            <span
              className={`recon-statecard__glyph recon-statecard__glyph--${card.tone}`}
              aria-hidden="true"
            />
          </div>
          <p className="console-card__value">
            {summary.counts[card.key]}
            <span className="recon-statecard__total"> / {total}</span>
          </p>
        </div>
      ))}
    </div>
  );
}

// ===========================================================================
// 3. Transaction ledger — the searchable, exportable side-by-side table
// ===========================================================================

/** A short delta string for the variance status pill (e.g. "▲ ₹-35"). */
function deltaLabel(match: ReconMatch): string {
  const currency = match.currency ?? FALLBACK_CURRENCY;
  const delta = Number(match.deltaMinor);
  const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "•";
  return `${arrow} ${formatMoney(delta, currency)}`;
}

/** Map a match state to a shared status-pill modifier + human label. */
function statusPill(state: ReconMatch["state"]): {
  readonly cls: string;
  readonly label: string;
} {
  switch (state) {
    case "matched":
      return { cls: "avos-status avos-status--ok", label: "matched" };
    case "variance":
      return { cls: "avos-status avos-status--attention", label: "variance" };
    case "disputed":
      return { cls: "avos-status avos-status--attention", label: "disputed" };
    case "unmatched":
      return { cls: "avos-status avos-status--critical", label: "unmatched" };
    case "pending":
    default:
      return { cls: "avos-status avos-status--pending", label: "pending" };
  }
}

/** Money cell: minor-string → formatted, or an em dash when null. */
function money(minor: string | null, currency: string | null): string {
  if (minor === null) return "—";
  return formatMoney(Number(minor), currency ?? FALLBACK_CURRENCY);
}

function LedgerRow({
  match,
  onClose,
  closingId,
}: {
  readonly match: ReconMatch;
  readonly onClose: (id: string) => void;
  readonly closingId: string | null;
}): ReactElement {
  const currency = match.currency ?? FALLBACK_CURRENCY;
  const reference = match.actualReference ?? match.expectedReference ?? "—";
  const guest = match.guestName ?? "—";
  const channel = match.channelName ?? "—";
  const pill = statusPill(match.state);
  const isClosed = match.status === "closed";
  const isClosing = closingId === match.id;

  return (
    <tr className={Number(match.deltaMinor) !== 0 ? "recon-row--flagged" : undefined}>
      <td>{reference}</td>
      <td>{guest}</td>
      <td>
        {match.channelName !== null ? (
          <span className="recon-channel">
            <span
              className="recon-channel__dot"
              style={{ background: channelColor(channel) }}
              aria-hidden="true"
            />
            {channel}
          </span>
        ) : (
          "—"
        )}
      </td>
      <td className="recon-num">{money(match.grossMinor, currency)}</td>
      <td className="recon-num">
        {match.commissionMinor !== null ? (
          <span className="recon-comm">
            -{formatMoney(Math.abs(Number(match.commissionMinor)), currency)}
          </span>
        ) : (
          "—"
        )}
      </td>
      <td className="recon-num">{money(match.expectedNetMinor, currency)}</td>
      <td className="recon-num">{money(match.actualNetMinor, currency)}</td>
      <td>
        <span className={pill.cls}>{pill.label}</span>
        {match.state === "variance" ? (
          <span className="recon-status-delta">{deltaLabel(match)}</span>
        ) : null}
        {!isClosed ? (
          <button
            type="button"
            className="console-btn console-btn--ghost"
            disabled={isClosing}
            onClick={() => onClose(match.id)}
            style={{ marginLeft: "0.5rem", padding: "0.2rem 0.55rem" }}
          >
            {isClosing ? "Closing…" : "Close"}
          </button>
        ) : (
          <span className="recon-state" style={{ marginLeft: "0.5rem" }}>
            closed
          </span>
        )}
      </td>
    </tr>
  );
}

/** Build a CSV string from the currently-filtered ledger rows. */
function toCsv(rows: readonly ReconMatch[]): string {
  const header = [
    "ref",
    "guest",
    "channel",
    "gross",
    "commission",
    "tax",
    "net",
    "bank",
    "delta",
    "status",
  ];
  const escape = (value: string): string => {
    if (/[",\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };
  const lines = rows.map((m) => {
    const cells = [
      m.actualReference ?? m.expectedReference ?? "",
      m.guestName ?? "",
      m.channelName ?? "",
      m.grossMinor ?? "",
      m.commissionMinor ?? "",
      m.taxMinor ?? "",
      m.expectedNetMinor ?? "",
      m.actualNetMinor ?? "",
      m.deltaMinor,
      m.state,
    ];
    return cells.map((c) => escape(String(c))).join(",");
  });
  return [header.join(","), ...lines].join("\n");
}

function LedgerPanel({
  matches,
  search,
  onSearch,
  onExport,
  onClose,
  closingId,
  seeding,
  running,
  onSeed,
  onRun,
}: {
  readonly matches: readonly ReconMatch[];
  readonly search: string;
  readonly onSearch: (value: string) => void;
  readonly onExport: () => void;
  readonly onClose: (id: string) => void;
  readonly closingId: string | null;
  readonly seeding: boolean;
  readonly running: boolean;
  readonly onSeed: () => void;
  readonly onRun: () => void;
}): ReactElement {
  const busy = seeding || running;
  return (
    <section className="console-panel" aria-label="Transaction ledger">
      <div className="recon-panelhead">
        <h2 className="console-panel__title">Transaction ledger</h2>
        <div className="recon-panelhead__actions">
          <button
            type="button"
            className="console-btn console-btn--ghost"
            disabled={busy}
            onClick={onSeed}
          >
            {seeding ? "Seeding…" : "Seed demo data"}
          </button>
          <button
            type="button"
            className="console-btn console-btn--ghost"
            disabled={busy}
            onClick={onRun}
          >
            {running ? "Running…" : "Run reconciliation"}
          </button>
          <button type="button" className="console-btn" onClick={onExport}>
            Export (CSV)
          </button>
        </div>
      </div>

      <div className="recon-toolbar" role="search">
        <div className="console-field recon-toolbar__search">
          <label htmlFor="recon-search">Search</label>
          <input
            id="recon-search"
            type="search"
            placeholder="Guest, reference, or channel"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
          />
        </div>
      </div>

      {matches.length > 0 ? (
        <table className="console-table">
          <caption className="console-sr-only">
            Side-by-side reconciliation of booking, expected net, and bank credit
          </caption>
          <thead>
            <tr>
              <th scope="col">Ref</th>
              <th scope="col">Guest</th>
              <th scope="col">Channel</th>
              <th scope="col">Gross</th>
              <th scope="col">Comm.</th>
              <th scope="col">Net</th>
              <th scope="col">Bank</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {matches.map((m) => (
              <LedgerRow
                key={m.id}
                match={m}
                onClose={onClose}
                closingId={closingId}
              />
            ))}
          </tbody>
        </table>
      ) : (
        <p className="console-empty">
          No transactions match. Seed demo data and run reconciliation, or clear
          your search.
        </p>
      )}
      <p className="recon-note">
        Export downloads the filtered rows as CSV. A Zoho Books push can be wired
        later.
      </p>
    </section>
  );
}

// ===========================================================================
// Existing DEPTH, kept as expandable <details>
// ===========================================================================

/** Present variance classification + cited evidence for a match (FR-4.7). */
function VarianceDetail({ match }: { readonly match: ReconMatch }): ReactElement | null {
  if (match.state !== "variance" || match.classification === null) {
    return null;
  }
  const classClass =
    match.classification === "anomalous"
      ? "recon-class recon-class--anomalous"
      : "recon-class recon-class--expected";
  const ev = match.evidence;
  const citation =
    ev === null
      ? null
      : ev.total === 0
        ? "no precedent"
        : `seen on ${ev.count} of ${ev.total} prior payouts on this channel`;
  return (
    <>
      <span className={classClass}>{match.classification}</span>
      {citation !== null ? <p className="recon-evidence">{citation}</p> : null}
    </>
  );
}

function VarianceEvidencePanel({
  matches,
}: {
  readonly matches: readonly ReconMatch[];
}): ReactElement {
  const variances = matches.filter((m) => m.state === "variance");
  return (
    <details className="recon-details">
      <summary>Variance evidence &amp; precedent ({variances.length})</summary>
      <p className="recon-caption">
        Each variance row carries its classification and the prior-payout
        precedent that supports it — the audit trail behind every flagged delta.
      </p>
      {variances.length > 0 ? (
        <table className="console-table">
          <caption className="console-sr-only">Variance classifications</caption>
          <thead>
            <tr>
              <th scope="col">Reference</th>
              <th scope="col">Expected net</th>
              <th scope="col">Actual net</th>
              <th scope="col">Delta</th>
              <th scope="col">State</th>
              <th scope="col">Classification</th>
            </tr>
          </thead>
          <tbody>
            {variances.map((m) => {
              const currency = m.currency ?? FALLBACK_CURRENCY;
              const delta = Number(m.deltaMinor);
              return (
                <tr key={m.id} className="recon-row--flagged">
                  <td>{m.actualReference ?? m.expectedReference ?? "—"}</td>
                  <td className="recon-num">
                    {money(m.expectedNetMinor, currency)}
                  </td>
                  <td className="recon-num">
                    {money(m.actualNetMinor, currency)}
                  </td>
                  <td className="recon-num">
                    <span className="recon-delta recon-delta--nonzero">
                      {formatMoney(delta, currency)}
                    </span>
                  </td>
                  <td>
                    <span className={stateBadgeClass(m.state)}>{m.state}</span>
                  </td>
                  <td>
                    <VarianceDetail match={m} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className="console-empty">No variances to explain.</p>
      )}
    </details>
  );
}

function ChannelsPanel({
  channels,
}: {
  readonly channels: readonly ReconChannel[];
}): ReactElement {
  return (
    <details className="recon-details">
      <summary>Channels &amp; commission model ({channels.length})</summary>
      <p className="recon-caption">
        The per-channel commission, tax, and fixed-fee model the expected ledger
        is computed from.
      </p>
      {channels.length > 0 ? (
        <table className="console-table">
          <caption className="console-sr-only">Channels</caption>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Kind</th>
              <th scope="col">Commission</th>
              <th scope="col">Tax</th>
              <th scope="col">Fixed fee</th>
            </tr>
          </thead>
          <tbody>
            {channels.map((c) => (
              <tr key={c.id}>
                <td>
                  <span className="recon-channel">
                    <span
                      className="recon-channel__dot"
                      style={{ background: channelColor(c.name) }}
                      aria-hidden="true"
                    />
                    {c.name}
                  </span>
                </td>
                <td>{c.kind}</td>
                <td className="recon-num">{formatBps(c.commissionBps)}</td>
                <td className="recon-num">{formatBps(c.taxBps)}</td>
                <td className="recon-num">
                  {formatMoney(Number(c.fixedFeeMinor), c.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="console-empty">No channels yet.</p>
      )}
    </details>
  );
}

function LedgersPanel({
  expected,
  actual,
}: {
  readonly expected: readonly ReconExpected[];
  readonly actual: readonly ReconActual[];
}): ReactElement {
  return (
    <details className="recon-details">
      <summary>
        Expected &amp; actual ledgers ({expected.length}/{actual.length})
      </summary>
      <p className="recon-caption">
        The raw ledger lines the match engine consumes: computed expected
        payouts and ingested actual payouts.
      </p>
      <div className="recon-ledgers">
        <div>
          <h3 className="console-panel__title">Expected payouts</h3>
          {expected.length > 0 ? (
            <table className="console-table">
              <caption className="console-sr-only">Expected payouts</caption>
              <thead>
                <tr>
                  <th scope="col">Reference</th>
                  <th scope="col">Gross</th>
                  <th scope="col">Commission</th>
                  <th scope="col">Tax</th>
                  <th scope="col">Fee</th>
                  <th scope="col">Expected net</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {expected.map((e) => (
                  <tr key={e.id}>
                    <td>{e.reference}</td>
                    <td className="recon-num">
                      {formatMoney(Number(e.grossMinor), e.currency)}
                    </td>
                    <td className="recon-num">
                      {formatMoney(Number(e.commissionMinor), e.currency)}
                    </td>
                    <td className="recon-num">
                      {formatMoney(Number(e.taxMinor), e.currency)}
                    </td>
                    <td className="recon-num">
                      {formatMoney(Number(e.feeMinor), e.currency)}
                    </td>
                    <td className="recon-num">
                      {formatMoney(Number(e.expectedNetMinor), e.currency)}
                    </td>
                    <td>
                      <span className="console-badge">{e.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="console-empty">No expected payouts yet.</p>
          )}
        </div>
        <div>
          <h3 className="console-panel__title">Actual payouts</h3>
          {actual.length > 0 ? (
            <table className="console-table">
              <caption className="console-sr-only">Actual payouts</caption>
              <thead>
                <tr>
                  <th scope="col">Reference</th>
                  <th scope="col">Net</th>
                  <th scope="col">Settled on</th>
                  <th scope="col">Source</th>
                </tr>
              </thead>
              <tbody>
                {actual.map((a) => (
                  <tr key={a.id}>
                    <td>{a.reference}</td>
                    <td className="recon-num">
                      {formatMoney(Number(a.netMinor), a.currency)}
                    </td>
                    <td>{a.settledOn ?? "—"}</td>
                    <td>{a.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="console-empty">No actual payouts yet.</p>
          )}
        </div>
      </div>
    </details>
  );
}

// ===========================================================================
// Top-level screen
// ===========================================================================

/** Zeroed flow used when apiReconFlow errors — the banner stays calm. */
const EMPTY_FLOW: ReconFlowSummary = {
  grossMinor: "0",
  commissionMinor: "0",
  taxMinor: "0",
  expectedNetMinor: "0",
  bankCreditMinor: "0",
  varianceMinor: "0",
  transactionCount: 0,
  currency: FALLBACK_CURRENCY,
};

interface ReconData {
  readonly summary: ReconSummary;
  readonly flow: ReconFlowSummary;
  readonly channels: readonly ReconChannel[];
  readonly expected: readonly ReconExpected[];
  readonly actual: readonly ReconActual[];
  readonly matches: readonly ReconMatch[];
}

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly data: ReconData }
  | { readonly kind: "error" };

/** Load every reconciliation dataset in parallel; flow falls back to zeros. */
async function loadAll(): Promise<ReconData> {
  const [summary, flowResult, channels, expected, actual, matches] =
    await Promise.all([
      apiReconSummary(),
      apiReconFlow(),
      apiListChannels(),
      apiListExpected(),
      apiListActual(),
      apiListMatches(),
    ]);
  const flow = asError(flowResult) !== null ? EMPTY_FLOW : (flowResult as ReconFlowSummary);
  return { summary, flow, channels, expected, actual, matches };
}

export default function ReconciliationScreen(): ReactElement {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [seeding, setSeeding] = useState(false);
  const [running, setRunning] = useState(false);
  const [closingId, setClosingId] = useState<string | null>(null);
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  const [status, setStatus] = useState<UserMessageModel | null>(null);
  const [search, setSearch] = useState("");

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const data = await loadAll();
      setState({ kind: "ready", data });
    } catch {
      setState({ kind: "error" });
    }
  }, []);

  useEffect(() => {
    let active = true;
    loadAll()
      .then((data) => {
        if (active) {
          setState({ kind: "ready", data });
        }
      })
      .catch(() => {
        if (active) {
          setState({ kind: "error" });
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const handleSeed = useCallback(async (): Promise<void> => {
    setApiError(null);
    setStatus(null);
    setSeeding(true);
    try {
      const result = await apiSeedReconDemo();
      const err = asError(result);
      if (err !== null) {
        setApiError(toUserMessage(err));
        return;
      }
      const seeded = result as ReconSeedResult;
      setStatus({
        what: "Demo data seeded.",
        why: `Created ${seeded.channels} channels, ${seeded.expected} expected and ${seeded.actual} actual payouts.`,
        next: "Run reconciliation to match them.",
      });
      await refresh();
    } catch {
      setApiError({
        what: "Could not seed demo data.",
        why: "The request to the reconciliation service failed.",
        next: "Check the API server is running and try again.",
      });
    } finally {
      setSeeding(false);
    }
  }, [refresh]);

  const handleRun = useCallback(async (): Promise<void> => {
    setApiError(null);
    setStatus(null);
    setRunning(true);
    try {
      const result = await apiRunReconciliation();
      const err = asError(result);
      if (err !== null) {
        setApiError(toUserMessage(err));
        return;
      }
      const run = result as ReconRunResult;
      setStatus({
        what: "Reconciliation complete.",
        why: `${run.matched} matched, ${run.variance} variance, ${run.unmatched} unmatched, ${run.pending} pending.`,
        next: "Review the ledger below and close the ones you have verified.",
      });
      await refresh();
    } catch {
      setApiError({
        what: "Could not run reconciliation.",
        why: "The request to the reconciliation service failed.",
        next: "Check the API server is running and try again.",
      });
    } finally {
      setRunning(false);
    }
  }, [refresh]);

  const handleClose = useCallback(
    async (id: string): Promise<void> => {
      setApiError(null);
      setStatus(null);
      setClosingId(id);
      try {
        const result = await apiCloseMatch(id);
        const err = asError(result);
        if (err !== null) {
          setApiError(toUserMessage(err));
          return;
        }
        await refresh();
      } catch {
        setApiError({
          what: "Could not close the match.",
          why: "The request to the reconciliation service failed.",
          next: "Check the API server is running and try again.",
        });
      } finally {
        setClosingId(null);
      }
    },
    [refresh],
  );

  const matches = state.kind === "ready" ? state.data.matches : [];

  // Client-side filter over guest / reference / channel.
  const filteredMatches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q === "") return matches;
    return matches.filter((m) => {
      const ref = (m.actualReference ?? m.expectedReference ?? "").toLowerCase();
      const guest = (m.guestName ?? "").toLowerCase();
      const channel = (m.channelName ?? "").toLowerCase();
      return ref.includes(q) || guest.includes(q) || channel.includes(q);
    });
  }, [matches, search]);

  // CSV export of the CURRENTLY-FILTERED rows. Blob/URL live here (SSR-safe).
  const handleExport = useCallback((): void => {
    if (typeof document === "undefined") return;
    const csv = toCsv(filteredMatches);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "reconciliation-ledger.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [filteredMatches]);

  return (
    <section
      aria-labelledby="reconciliation-heading"
      data-console-screen="reconciliation"
    >
      <p className="console-page-eyebrow">Finance · The killer feature</p>
      <h1 id="reconciliation-heading" className="console-page-title">
        Payment Reconciliation
      </h1>
      <p className="console-lede">
        Reconstruct every payout from OTA booking through commission and tax to
        the bank credit, and isolate any net that did not arrive.
      </p>

      {state.kind === "loading" ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading reconciliation…
        </p>
      ) : null}

      {state.kind === "error" ? (
        <p className="console-empty" role="status">
          Reconciliation data is unavailable right now. Try refreshing in a
          moment.
        </p>
      ) : null}

      {state.kind === "ready" ? (
        <>
          <FlowBanner flow={state.data.flow} />

          <StateCards summary={state.data.summary} />

          {apiError !== null ? (
            <UserMessage message={apiError} severity="error" />
          ) : null}
          {status !== null ? (
            <UserMessage message={status} severity="status" />
          ) : null}

          <LedgerPanel
            matches={filteredMatches}
            search={search}
            onSearch={setSearch}
            onExport={handleExport}
            onClose={handleClose}
            closingId={closingId}
            seeding={seeding}
            running={running}
            onSeed={handleSeed}
            onRun={handleRun}
          />

          <VarianceEvidencePanel matches={state.data.matches} />

          <ChannelsPanel channels={state.data.channels} />

          <LedgersPanel
            expected={state.data.expected}
            actual={state.data.actual}
          />
        </>
      ) : null}
    </section>
  );
}
